import { get } from '@shared/adapters/lodash'
import { formatMoney, formatPercentage, formatDisplayDate } from '@shared/utils'
import type { Investment } from '../../types/investmentTypes'
import type {
  PortfolioGraphRental,
  PortfolioGraphValue,
  PortfolioHoldingsSummary,
  PortfolioOverview,
  RentalIncomeSummary,
} from '../../types/portfolioTypes'

const DEFAULT_CURRENCY = 'AED'
const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 10

// Per-shape helpers keep each parser function small and reduce the
// repeated `get(item, 'a.b') ?? default` complexity ESLint flagged.

const parseMoney = (resp: unknown, key: string) => ({
  value: get(resp, `${key}.value`) ?? 0,
  currency: get(resp, `${key}.currency`) ?? DEFAULT_CURRENCY,
})

const parseMoneyWithChange = (resp: unknown, key: string) => ({
  ...parseMoney(resp, key),
  changePercentage:
    get(resp, `${key}.changePercentage`) ?? get(resp, `${key}.change_percentage`) ?? 0,
})

interface ParsedHolding {
  propertyId: string
  offeringId: string
  propertyName: string
  propertyLocation: string
  coverImage: string
  tokensHeld: number
  currentInvestmentValue: number
  currency: string
  totalReturns: { amount: number; currency: string; percentage: number }
  createdAt: string
  updatedAt: string
}

const parseHolding = (item: unknown): ParsedHolding => ({
  propertyId: get(item, 'propertyId') ?? '',
  offeringId: get(item, 'offeringId') ?? '',
  propertyName: get(item, 'propertyName') ?? '',
  propertyLocation: get(item, 'propertyLocation') ?? '',
  coverImage: get(item, 'coverImage') ?? '',
  tokensHeld: Number(get(item, 'tokensHeld') ?? 0),
  currentInvestmentValue: Number(get(item, 'currentInvestmentValue') ?? 0),
  currency: get(item, 'currency') ?? DEFAULT_CURRENCY,
  totalReturns: {
    amount: Number(get(item, 'totalReturns.amount') ?? 0),
    currency: get(item, 'totalReturns.currency') ?? get(item, 'currency') ?? DEFAULT_CURRENCY,
    percentage: Number(get(item, 'totalReturns.percentage') ?? 0),
  },
  createdAt: get(item, 'createdAt') ?? '',
  updatedAt: get(item, 'updatedAt') ?? '',
})

const holdingToInvestmentCard = (holding: ParsedHolding): Investment => {
  const investedAmount = Math.max(holding.currentInvestmentValue - holding.totalReturns.amount, 0)
  const isPositive = holding.totalReturns.amount >= 0

  return {
    id: holding.propertyId,
    offeringId: holding.offeringId,
    imageUrl: holding.coverImage,
    yieldRate: formatPercentage(holding.totalReturns.percentage),
    tokens: holding.tokensHeld,
    location: holding.propertyLocation,
    title: holding.propertyName,
    invested: formatMoney(holding.currency, investedAmount),
    currentValue: formatMoney(holding.currency, holding.currentInvestmentValue),
    currentValuePositive: isPositive,
    totalReturn: formatMoney(holding.totalReturns.currency, holding.totalReturns.amount, true),
    totalReturnPositive: isPositive,
    acquiredLabel: holding.createdAt ? formatDisplayDate(new Date(holding.createdAt)) : undefined,
  }
}

export const parsePortfolioOverview = (resp: unknown): PortfolioOverview => {
  const data = get(resp, 'data') ?? resp
  return {
    totalInvested: parseMoney(data, 'totalInvested'),
    currentPortfolioValue: parseMoneyWithChange(data, 'currentPortfolioValue'),
    totalRentalIncome: parseMoneyWithChange(data, 'totalRentalIncome'),
    projectedRoi: {
      percentage: get(data, 'projectedRoi.percentage') ?? 0,
    },
    lastUpdatedAt: get(data, 'lastUpdatedAt') ?? '',
  }
}

export const parsePortfolioGraphValue = (resp: unknown): PortfolioGraphValue => {
  const data = get(resp, 'data') ?? resp
  return {
    type: 'value',
    currency: get(data, 'currency') ?? DEFAULT_CURRENCY,
    startDate: get(data, 'startDate') ?? get(data, 'start_date') ?? '',
    series: ((get(data, 'series') ?? []) as unknown[]).map((point) => ({
      month: get(point, 'month') ?? '',
      investedAmount: get(point, 'investedAmount') ?? get(point, 'invested_amount') ?? 0,
      portfolioValue: get(point, 'portfolioValue') ?? get(point, 'portfolio_value') ?? 0,
    })),
  }
}

export const parsePortfolioGraphRental = (resp: unknown): PortfolioGraphRental => {
  const data = get(resp, 'data') ?? resp
  return {
    type: 'rental',
    currency: get(data, 'currency') ?? DEFAULT_CURRENCY,
    startDate: get(data, 'startDate') ?? get(data, 'start_date') ?? '',
    series: ((get(data, 'series') ?? []) as unknown[]).map((point) => ({
      month: get(point, 'month') ?? '',
      rentalIncome: get(point, 'rentalIncome') ?? get(point, 'rental_income') ?? 0,
    })),
  }
}

export const parsePortfolioHoldingsSummary = (resp: unknown): PortfolioHoldingsSummary => {
  const data = get(resp, 'data') ?? resp
  return {
    totalTokensHeld: get(data, 'totalTokensHeld') ?? get(data, 'total_tokens_held') ?? 0,
    propertiesInvested: get(data, 'propertiesInvested') ?? get(data, 'properties_invested') ?? 0,
    totalPortfolioValue:
      get(data, 'totalPortfolioValue') ?? get(data, 'total_portfolio_value') ?? 0,
    currency: get(data, 'currency') ?? DEFAULT_CURRENCY,
  }
}

export const parseRentalIncomeSummary = (resp: unknown): RentalIncomeSummary => {
  const data = get(resp, 'data') ?? resp
  const last = get(data, 'lastReceived') ?? {}
  const next = get(data, 'nextMonth') ?? {}

  const lastCurrency = String(get(last, 'currencyCode') ?? DEFAULT_CURRENCY)
  const nextCurrency = String(get(next, 'currencyCode') ?? DEFAULT_CURRENCY)
  const lastReceivedAt: string | null = get(last, 'receivedAt') ?? null
  const nextExpectedAt: string = String(get(next, 'expectedAt') ?? '')

  return {
    lastReceived: {
      amount: formatMoney(lastCurrency, Number(get(last, 'amount') ?? 0)),
      receivedAt: lastReceivedAt ? formatDisplayDate(new Date(lastReceivedAt)) : null,
    },
    nextMonth: {
      amount: formatMoney(nextCurrency, Number(get(next, 'amount') ?? 0)),
      expectedAt: nextExpectedAt ? formatDisplayDate(new Date(nextExpectedAt)) : '',
    },
  }
}

export const parsePortfolioHoldings = (
  resp: unknown,
): { rows: Investment[]; page: number; limit: number; rowsCount: number } => {
  const rows = (get(resp, 'data.rows') ?? []) as unknown[]
  return {
    rows: rows.map(parseHolding).map(holdingToInvestmentCard),
    page: Number(get(resp, 'data.page') ?? DEFAULT_PAGE),
    limit: Number(get(resp, 'data.limit') ?? DEFAULT_PAGE_SIZE),
    rowsCount: Number(get(resp, 'data.rowsCount') ?? rows.length ?? 0),
  }
}
